import Markdown from "react-markdown";
import { Link } from "react-router";

import { ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  enrichmentEntityHref,
  enrichmentEntityLabel,
  enrichmentReviewHref,
  isLastAttempt,
  parkAttemptLabel,
  parkRetriesLeft,
} from "@/lib/tina/enrichment-jobs";
import { ConfidenceMeter } from "@/pages/tina/enrichment/components/confidence-meter";
import {
  KeyValueGrid,
  type KeyValueRow,
} from "@/pages/tina/items/components/key-value-grid";
import type { EnrichmentLowConfidenceProblem } from "@/types/tina";

/**
 * What the curator reads before deciding, for an `enrichment_low_confidence`
 * park.
 *
 * Rendered from `HitlContextView`'s content-type branch, above the actions. The
 * scrape branches (music/cinema/theater) key off `hitl_context`'s stage
 * outputs; an enrichment context has none of those, and before this branch
 * existed it fell through to "No structured HITL context available".
 */
export function EnrichmentContextView({
  problem,
}: {
  problem: EnrichmentLowConfidenceProblem;
}) {
  const entityHref = enrichmentEntityHref(
    problem.entity_type,
    problem.entity_id,
  );
  const reviewHref = enrichmentReviewHref(
    problem.entity_type,
    problem.entity_id,
  );
  const sources = problem.enrichment_sources ?? [];
  const parkCount = problem.park_count;
  // Both halves of the copy count off `parkAttemptNumber`, so the label and the
  // tail cannot disagree about an absent count the way they used to: the label
  // floored it at 1 while the tail read a literal `0` as "3 retries left".
  const retriesLeft = parkRetriesLeft(parkCount);

  const rows: KeyValueRow[] = [
    {
      label: "Entity",
      value: (
        <span className="inline-flex flex-wrap items-center gap-2">
          <Badge className="bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
            {enrichmentEntityLabel(problem.entity_type)}
          </Badge>
          {entityHref ? (
            <a
              href={entityHref}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-[13px] text-blue-500 hover:underline"
            >
              {problem.entity_id}
            </a>
          ) : (
            <span className="font-mono text-[13px]">{problem.entity_id}</span>
          )}
          {reviewHref && (
            <a
              href={reviewHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center text-xs text-blue-500 hover:underline"
            >
              open the full review page
              <ArrowUpRight className="ml-0.5 h-3 w-3" />
            </a>
          )}
        </span>
      ),
    },
    {
      label: "Confidence",
      value: (
        <ConfidenceMeter
          confidence={problem.confidence}
          threshold={problem.threshold}
          className="max-w-xs"
        />
      ),
    },
    {
      label: "Attempt",
      value: (
        <span className="text-sm">
          {parkAttemptLabel(parkCount)}
          <span className="ml-2 text-xs text-muted-foreground">
            {isLastAttempt(parkCount)
              ? `last attempt — another low-confidence result closes the job for manual review`
              : `${retriesLeft} retr${retriesLeft === 1 ? "y" : "ies"} left`}
          </span>
        </span>
      ),
    },
  ];

  if (sources.length > 0) {
    rows.push({
      label: "Sources",
      value: (
        <span className="flex flex-wrap gap-1">
          {sources.map((source) => (
            <Badge key={source} variant="secondary" className="font-normal">
              {source}
            </Badge>
          ))}
        </span>
      ),
    });
  }

  if (problem.origin_event_id) {
    rows.push({
      label: "Origin",
      value: (
        <span className="inline-flex items-center gap-2 text-sm">
          <Link
            to={`/tina/items/show/${problem.origin_event_id}`}
            className="font-mono text-[13px] text-blue-500 hover:underline"
          >
            {problem.origin_event_id}
          </Link>
          <span className="text-xs text-muted-foreground">
            found while scraping this event
          </span>
        </span>
      ),
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Enrichment context
      </h3>
      <KeyValueGrid rows={rows} />
      {problem.reasoning && (
        <div className="flex flex-col gap-1 rounded-md border bg-muted/20 p-3">
          <h4 className="text-xs uppercase tracking-wide text-muted-foreground">
            Model reasoning
          </h4>
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
            <Markdown>{problem.reasoning}</Markdown>
          </div>
        </div>
      )}
    </div>
  );
}
